import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { trpc } from './trpc'

/**
 * **My Work im Browser** ([Z-24](../../../../docs/phasen/zusatz-24-my-work.md)) – Termin-Bezüge und
 * Faden. Wie die Markierung: eine kleine Abfrage, optimistisch geändert, danach neu geholt (kein
 * Live-Kanal, E-416).
 */

type Bezuege = Awaited<ReturnType<typeof trpc.mywork.bezuege.query>>
type Faeden = Awaited<ReturnType<typeof trpc.mywork.faeden.query>>

export const bezuegeQuery = (von: string, bis: string) =>
  queryOptions({
    queryKey: ['mywork', 'bezuege', von, bis] as const,
    queryFn: () => trpc.mywork.bezuege.query({ von, bis }),
    staleTime: 30_000,
  })

export const serienBezuegeQuery = (serieId: string) =>
  queryOptions({
    queryKey: ['mywork', 'serie', serieId] as const,
    queryFn: () => trpc.mywork.bezuegeDerSerie.query({ serieId }),
    staleTime: 60_000,
  })

export const faedenQuery = queryOptions({
  queryKey: ['mywork', 'faeden'] as const,
  queryFn: () => trpc.mywork.faeden.query(),
  staleTime: 30_000,
})

export interface TerminKennung {
  id: string
  serie: string | null
  /** Ortszeit, z. B. `2026-10-23T13:30:00.0000000` – der Server kürzt auf Sekunden. */
  beginn: string
  titel: string
}

/** Eine Aufgabe fest an einen Termin legen (oder lösen) – sofort sichtbar, danach neu geholt. */
export function useBezug(tag: string) {
  const qc = useQueryClient()
  const schluessel = bezuegeQuery(tag, tag).queryKey
  const nachher = () => void qc.invalidateQueries({ queryKey: ['mywork'] })

  const setzen = useMutation({
    mutationFn: (v: { termin: TerminKennung; taskId: string; quelle: 'festhalten' | 'hand' }) =>
      trpc.mywork.bezugSetzen.mutate(v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: schluessel })
      const vorher = qc.getQueryData<Bezuege>(schluessel)
      qc.setQueryData<Bezuege>(schluessel, (alt) => [
        ...(alt ?? []).filter((b) => !(b.terminId === v.termin.id && b.taskId === v.taskId)),
        {
          id: `neu-${v.taskId}`,
          terminId: v.termin.id,
          serieId: v.termin.serie,
          terminBeginn: v.termin.beginn.slice(0, 19),
          terminTitel: v.termin.titel,
          taskId: v.taskId,
          quelle: v.quelle,
        },
      ])
      return vorher
    },
    onError: (_f, _v, vorher) => qc.setQueryData(schluessel, vorher),
    onSettled: nachher,
  })

  const loesen = useMutation({
    mutationFn: (v: { terminId: string; taskId: string }) => trpc.mywork.bezugLoesen.mutate(v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: schluessel })
      const vorher = qc.getQueryData<Bezuege>(schluessel)
      qc.setQueryData<Bezuege>(schluessel, (alt) =>
        (alt ?? []).filter((b) => !(b.terminId === v.terminId && b.taskId === v.taskId)),
      )
      return vorher
    },
    onError: (_f, _v, vorher) => qc.setQueryData(schluessel, vorher),
    onSettled: nachher,
  })

  return { setzen, loesen }
}

/** Alle Fäden als Karte `taskId → Faden`. */
export function useFaeden() {
  const { data = [] } = useQuery(faedenQuery)
  return new Map(data.map((f) => [f.taskId, f]))
}

export function useFadenSetzen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { taskId: string; stand: string; naechsterSchritt: string }) =>
      trpc.mywork.fadenSetzen.mutate(v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: faedenQuery.queryKey })
      const vorher = qc.getQueryData<Faeden>(faedenQuery.queryKey)
      qc.setQueryData<Faeden>(faedenQuery.queryKey, (alt) => {
        const rest = (alt ?? []).filter((f) => f.taskId !== v.taskId)
        return v.stand || v.naechsterSchritt
          ? [...rest, { ...v, aktualisiertAm: new Date().toISOString() }]
          : rest
      })
      return vorher
    },
    onError: (_f, _v, vorher) => qc.setQueryData(faedenQuery.queryKey, vorher),
    onSettled: () => void qc.invalidateQueries({ queryKey: faedenQuery.queryKey }),
  })
}

/* ── Notizbuch am Termin und offene Enden (AP 24.18, 24.25) ── */

export interface TerminFuerNotizbuch {
  schluessel: string
  titel: string
  beginn: string
  serie: string | null
}

export type Tageslage = Awaited<ReturnType<typeof trpc.notizbuch.mywork.tageslage.query>>
export type TerminLage = Tageslage['termine'][string]
export type OffenerVorschlag = Awaited<
  ReturnType<typeof trpc.notizbuch.mywork.offeneVorschlaege.query>
>[number]

/** Was das Notizbuch zu den Terminen des Tages weiß – ohne Freigabe kommt eine leere Antwort. */
export const tageslageQuery = (tag: string, termine: readonly TerminFuerNotizbuch[]) =>
  queryOptions({
    queryKey: ['mywork', 'tageslage', tag, termine.map((t) => t.schluessel).join('|')] as const,
    queryFn: () => trpc.notizbuch.mywork.tageslage.query({ tag, termine: [...termine] }),
    staleTime: 60_000,
  })

export const offeneVorschlaegeQuery = queryOptions({
  queryKey: ['mywork', 'offeneVorschlaege'] as const,
  queryFn: () => trpc.notizbuch.mywork.offeneVorschlaege.query({ seitTagen: 7 }),
  staleTime: 60_000,
})

/**
 * **Verworfene Vorschläge** – je Gerät, wie im Blatt des Notizbuchs. Schlüssel `<notiz>:<text>`.
 * Ziel laut Konzept: in den verschlüsselten `zustand/` des Notizbuchs, damit es überall gilt.
 */
const VERWORFEN = 'netcon-work:mywork-verworfen'

export function verworfenLesen(): Set<string> {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(VERWORFEN) ?? '[]') as string[])
  } catch {
    return new Set()
  }
}

export function verworfenMerken(schluessel: string): void {
  try {
    const alle = [...verworfenLesen(), schluessel].slice(-500)
    window.localStorage.setItem(VERWORFEN, JSON.stringify(alle))
  } catch {
    // Ohne Gerätespeicher bleibt der Vorschlag beim nächsten Laden wieder stehen.
  }
}

/* ── Mitschrift im Termin und Notizen zur Aufgabe (AP 24.23, 24.22) ── */

export interface BlattKennung {
  notizbuchId: string
  notizId: string
}

export type MitschriftZeile = Awaited<
  ReturnType<typeof trpc.notizbuch.mywork.mitschrift.query>
>[number] & {
  /** Noch nicht vom Server bestätigt. */
  wartet?: boolean
}

/** Die Zeilen mit Zeitmarke aus dem Blatt eines Termins – je Termin (Outlook-Kennung) gemerkt. */
export const mitschriftQuery = (terminId: string, blatt: BlattKennung | null | undefined) =>
  queryOptions({
    queryKey: ['mywork', 'mitschrift', terminId] as const,
    queryFn: (): Promise<MitschriftZeile[]> =>
      blatt ? trpc.notizbuch.mywork.mitschrift.query(blatt) : Promise.resolve([]),
    enabled: !!blatt,
    staleTime: 30_000,
  })

export interface MitschreibenEingabe {
  termin: {
    id: string
    titel: string
    beginn: string
    ende: string
    teilnehmer?: string[]
    serie?: string | null
  }
  blatt: BlattKennung | null | undefined
  bevorzugt: string | null | undefined
  /** Eine Zeile Markdown – ohne Spiegelstrich, den setzt der Server. */
  text: string
}

/**
 * **Eine Zeile ins Blatt des Termins** – sofort sichtbar, der Server hängt sie hinterher an
 * (Leitplanke 1). Nacheinander geschickt, damit zwei schnelle Zeilen nicht zwei Blätter anlegen.
 * Bei einem Fehler verschwindet die Zeile wieder und das Versprechen schlägt fehl – der Aufrufer
 * stellt den Text zurück ins Feld.
 */
export function useMitschreiben() {
  const qc = useQueryClient()
  const kette = useRef<Promise<unknown>>(Promise.resolve())
  return useCallback(
    async (e: MitschreibenEingabe): Promise<void> => {
      const schluessel = mitschriftQuery(e.termin.id, null).queryKey
      const vorlaeufig: MitschriftZeile = {
        text: e.text,
        marke: new Date().toISOString(),
        wartet: true,
      }
      qc.setQueryData<MitschriftZeile[]>(schluessel, (alt) => [...(alt ?? []), vorlaeufig])
      const beginn = new Date(e.termin.beginn.slice(0, 19))
      const lauf = kette.current
        .catch(() => undefined)
        .then(() =>
          trpc.notizbuch.mywork.mitschreiben.mutate({
            termin: {
              titel: e.termin.titel,
              beginn: e.termin.beginn.slice(0, 19),
              ende: e.termin.ende.slice(0, 19),
              ...(e.termin.teilnehmer?.length ? { teilnehmer: e.termin.teilnehmer } : {}),
              ...(e.termin.serie ? { serie: e.termin.serie } : {}),
            },
            blatt: e.blatt ?? null,
            bevorzugt: e.bevorzugt ?? null,
            ...(Number.isNaN(beginn.getTime()) ? {} : { datum: beginn.toISOString() }),
            text: e.text,
          }),
        )
      kette.current = lauf
      try {
        const r = await lauf
        qc.setQueryData<MitschriftZeile[]>(schluessel, (alt) => [
          ...r.zeilen,
          // Was danach getippt wurde und noch unterwegs ist, bleibt stehen.
          ...(alt ?? []).filter((z) => z.wartet && z !== vorlaeufig && z.marke > vorlaeufig.marke),
        ])
        if (r.neu) void qc.invalidateQueries({ queryKey: ['mywork', 'tageslage'] })
      } catch (f) {
        qc.setQueryData<MitschriftZeile[]>(schluessel, (alt) =>
          (alt ?? []).filter((z) => z !== vorlaeufig),
        )
        throw f
      }
    },
    [qc],
  )
}

export type NotizZurAufgabe = Awaited<
  ReturnType<typeof trpc.notizbuch.mywork.zurAufgabe.query>
>[number]

export const notizenZurAufgabeQuery = (aufgabeId: string) =>
  queryOptions({
    queryKey: ['mywork', 'zurAufgabe', aufgabeId] as const,
    queryFn: () => trpc.notizbuch.mywork.zurAufgabe.query({ aufgabeId }),
    staleTime: 60_000,
  })
